import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faTrash, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import UnitMappingService from '../services/unitMappingService';
import RecipesService from '../services/recipesService';
import './ComplimentaryRecipeFinalization.css';

/**
 * Complimentary Recipe Finalization Page
 * Phase 2: Mandatory Onboarding Gate (MOG)
 * 
 * This page blocks access to the dashboard until all complimentary items
 * have their ingredients defined (recipe_status = COMPLETE).
 */
const ComplimentaryRecipeFinalization = ({ onComplete, onBack }) => {
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [messageModal, setMessageModal] = useState({ open: false, type: 'info', title: '', message: '', onOk: null });

  const showMessage = (type, title, message, onOk = null) => {
    setMessageModal({ open: true, type, title, message, onOk });
  };

  const closeMessage = () => {
    const callback = messageModal.onOk;
    setMessageModal({ open: false, type: 'info', title: '', message: '', onOk: null });
    if (callback) callback();
  };

  // Load pending complimentary items on mount
  useEffect(() => {
    loadPendingItems();
    loadUnitOptions();
  }, []);

  const loadPendingItems = async () => {
    try {
      setLoading(true);
      const businessId = UnitMappingService.getBusinessId();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${apiUrl}/api/complimentary-tracking/pending?businessId=${businessId}`);
      const data = await response.json();

      if (data.success) {
        setPendingItems(data.items || []);
        
        // Auto-select first item if available
        if (data.items && data.items.length > 0) {
          selectItem(data.items[0]);
        }
      } else {
        setError(data.error || 'Failed to load pending items');
      }
    } catch (err) {
      console.error('Error loading pending items:', err);
      setError('Failed to load pending items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUnitOptions = async () => {
    try {
      const options = await UnitMappingService.getUnitOptions();
      // Use API result - flatten all categories into a single list
      const allUnits = [];
      const seen = new Set();
      for (const category of Object.values(options || {})) {
        for (const u of (Array.isArray(category) ? category : [])) {
          const sym = u.unit_symbol || u.symbol || u.value || u;
          if (sym && !seen.has(sym)) {
            seen.add(sym);
            allUnits.push({ value: sym, label: u.unit_name || sym });
          }
        }
      }
      // Ensure base units are always present
      for (const base of ['g', 'kg', 'ml', 'L', 'pcs', 'unit']) {
        if (!seen.has(base)) allUnits.push({ value: base, label: base });
      }
      setUnitOptions(allUnits.length > 0 ? allUnits : [
        { value: 'g', label: 'g' },
        { value: 'kg', label: 'kg' },
        { value: 'ml', label: 'ml' },
        { value: 'L', label: 'L' }
      ]);
    } catch (err) {
      console.warn('Failed to load unit options:', err);
      setUnitOptions([
        { value: 'g', label: 'g' },
        { value: 'kg', label: 'kg' },
        { value: 'ml', label: 'ml' },
        { value: 'L', label: 'L' }
      ]);
    }
  };

  const selectItem = async (item) => {
    setSelectedItem(item);
    
    // Load existing ingredients if any
    try {
      const businessId = UnitMappingService.getBusinessId();
      const existingIngredients = await RecipesService.getRecipeIngredients(item.menu_item_id, businessId);
      
      if (existingIngredients && existingIngredients.length > 0) {
        setIngredients(existingIngredients.map(ing => ({
          name: ing.ingredient_name || ing.name,
          quantity: ing.quantity,
          unit: ing.unit || 'g',
          id: ing.id
        })));
      } else {
        // Start with one empty ingredient row
        setIngredients([{ name: '', quantity: '', unit: 'g' }]);
      }
    } catch (err) {
      console.warn('No existing ingredients, starting fresh:', err);
      setIngredients([{ name: '', quantity: '', unit: 'g' }]);
    }
  };

  const removeItem = (itemToRemove) => {
    const updatedItems = pendingItems.filter(item => item.menu_item_id !== itemToRemove.menu_item_id);
    setPendingItems(updatedItems);
    
    // If the removed item was selected, select the next item
    if (selectedItem?.menu_item_id === itemToRemove.menu_item_id) {
      if (updatedItems.length > 0) {
        selectItem(updatedItems[0]);
      } else {
        setSelectedItem(null);
        setIngredients([{ name: '', quantity: '', unit: 'g' }]);
      }
    }
  };

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', quantity: '', unit: 'g' }]);
  };

  const removeIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const updateIngredient = (index, field, value) => {
    const updated = ingredients.map((ing, i) => 
      i === index ? { ...ing, [field]: field === 'quantity' ? parseFloat(value) || 0 : value } : ing
    );
    setIngredients(updated);
  };

  const validateIngredients = () => {
    // Must have at least one ingredient with name and quantity
    const valid = ingredients.some(ing => 
      ing.name && ing.name.trim() !== '' && 
      ing.quantity && parseFloat(ing.quantity) > 0
    );

    if (!valid) {
      showMessage('error', 'Validation Error', 'At least one ingredient with a valid name and quantity is required.');
      return false;
    }

    return true;
  };

  const saveIngredients = async () => {
    if (!validateIngredients()) {
      return;
    }

    try {
      setSaving(true);
      const businessId = UnitMappingService.getBusinessId();

      // Filter out empty ingredients
      const validIngredients = ingredients.filter(ing => 
        ing.name && ing.name.trim() !== '' && 
        ing.quantity && parseFloat(ing.quantity) > 0
      );

      // Save ingredients to database
      await RecipesService.updateRecipeIngredients(
        selectedItem.menu_item_id,
        validIngredients,
        businessId
      );

      // Mark item as complete
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await fetch(`${apiUrl}/api/complimentary-tracking/complete/${selectedItem.menu_item_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-business-id': businessId.toString()
        },
        body: JSON.stringify({ businessId })
      });

      // Update completed count
      setCompletedCount(prev => prev + 1);

      // Reload pending items
      await loadPendingItems();

      // Show success message
      showMessage('success', 'Recipe Saved', `Recipe for "${selectedItem.name}" saved successfully!`);

    } catch (err) {
      console.error('Error saving ingredients:', err);
      showMessage('error', 'Save Failed', 'Failed to save ingredients. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const completeOnboarding = () => {
    if (pendingItems.length === 0) {
      // All items complete - release MOG
      showMessage('success', 'Setup Complete', 'Invexis is Now 100% Financially Calibrated!', () => {
        if (onComplete) {
          onComplete();
        }
      });
    } else {
      showMessage('warning', 'Incomplete Setup', `${pendingItems.length} item(s) still need ingredient definition.`);
    }
  };

  if (loading) {
    return (
      <div className="finalization-container">
        <div className="finalization-loading">
          <div className="spinner"></div>
          <p>Loading pending complimentary items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="finalization-container">
        <div className="finalization-error">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" color="#e74c3c" />
          <h2>Error Loading Items</h2>
          <p>{error}</p>
          <button onClick={loadPendingItems} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (pendingItems.length === 0) {
    return (
      <div className="finalization-container">
        <div className="finalization-complete">
          <FontAwesomeIcon icon={faCheckCircle} size="4x" color="#27ae60" />
          <h1>🎉 Setup Complete!</h1>
          <h2>Invexis is Now 100% Financially Calibrated</h2>
          <p>All complimentary items have been defined with ingredients.</p>
          <p>You can now access the full dashboard and start managing your inventory.</p>
          <button onClick={completeOnboarding} className="btn-complete">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="finalization-container">
      {/* Header */}
      <div className="finalization-header">
        <div className="header-content">
          <FontAwesomeIcon icon={faExclamationTriangle} size="2x" color="#f39c12" />
          <div className="header-text">
            <h1>Complimentary Recipe Finalization</h1>
            <p className="header-subtitle">
              Define ingredients for complimentary items to complete setup
            </p>
          </div>
        </div>
        <div className="progress-badge">
          {completedCount > 0 && (
            <span className="completed-count">
              {completedCount} completed
            </span>
          )}
          <span className="pending-count">
            {pendingItems.length} pending
          </span>
        </div>
      </div>

      {/* MOG Warning Banner */}
      <div className="mog-banner">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <div className="mog-text">
          <strong>Mandatory Onboarding Gate (MOG)</strong>
          <p>
            Dashboard access is blocked until all {pendingItems.length} complimentary item(s) 
            have their ingredients defined. This ensures accurate cost tracking.
          </p>
        </div>
      </div>

      <div className="finalization-content">
        {/* Left Panel: Pending Items List */}
        <div className="pending-items-panel">
          <h3>Pending Items ({pendingItems.length})</h3>
          <div className="items-list">
            {pendingItems.map((item) => (
              <div
                key={item.menu_item_id}
                className={`item-card ${selectedItem?.menu_item_id === item.menu_item_id ? 'selected' : ''}`}
                onClick={() => selectItem(item)}
              >
                <button 
                  className="item-remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem(item);
                  }}
                  aria-label="Remove item"
                >
                  ×
                </button>
                {item.image_url && (
                  <img src={item.image_url} alt={item.name} className="item-image" />
                )}
                <div className="item-info">
                  <h4>{item.name}</h4>
                  {item.parent_dish_name && (
                    <p className="parent-dish">Served with: {item.parent_dish_name}</p>
                  )}
                  <span className="status-badge pending">
                    Needs Ingredients
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel: Ingredient Editor */}
        <div className="ingredient-editor-panel">
          {selectedItem ? (
            <>
              <div className="editor-header">
                <h2>Define Recipe: {selectedItem.name}</h2>
                <p className="editor-subtitle">
                  Enter ingredients for <strong>ONE STANDARD SERVING</strong>
                </p>
              </div>

              <div className="ingredients-section">
                <div className="ingredients-header">
                  <h3>Ingredients</h3>
                  <button onClick={addIngredient} className="btn-add-ingredient">
                    <FontAwesomeIcon icon={faPlus} /> Add Ingredient
                  </button>
                </div>

                <div className="ingredients-list">
                  {ingredients.map((ingredient, index) => (
                    <div key={index} className="ingredient-row">
                      <input
                        type="text"
                        placeholder="Ingredient name (e.g., Groundnut)"
                        value={ingredient.name}
                        onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                        className="input-name"
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={ingredient.quantity}
                        onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                        className="input-quantity"
                        min="0"
                        step="0.01"
                      />
                      <select
                        value={ingredient.unit}
                        onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                        className="input-unit"
                      >
                        {unitOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {ingredients.length > 1 && (
                        <button
                          onClick={() => removeIngredient(index)}
                          className="btn-remove"
                          title="Remove ingredient"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="constraint-notice">
                  <FontAwesomeIcon icon={faExclamationTriangle} color="#f39c12" />
                  <p>
                    <strong>Constraint:</strong> At least one ingredient with a valid quantity 
                    must be defined to save this recipe.
                  </p>
                </div>
              </div>

              <div className="editor-actions">
                <button
                  onClick={saveIngredients}
                  disabled={saving}
                  className="btn-save"
                >
                  {saving ? (
                    <>
                      <div className="spinner-small"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      Save Recipe
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>← Select a complimentary item from the list to define its recipe</p>
            </div>
          )}
        </div>
      </div>

      {/* Message Modal */}
      {messageModal.open && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: 'min(400px, 90vw)',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              {messageModal.type === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#22c55e" />
              )}
              {messageModal.type === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
              )}
              {messageModal.type === 'warning' && (
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
              )}
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              {messageModal.title}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#4b5563',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              {messageModal.message}
            </div>
            <button
              onClick={closeMessage}
              style={{
                padding: '10px 24px',
                backgroundColor: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '120px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplimentaryRecipeFinalization;
